// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Anchor, Button, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import { useMedplum } from '@medplum/react';
import { IconArrowLeft, IconMail } from '@tabler/icons-react';
import { useState, useCallback } from 'react';
import type { JSX } from 'react';
import { Link } from 'react-router';
import { AuthLayout } from '../components/auth/AuthLayout';
import classes from './ForgotPasswordPage.module.css';

interface ForgotPasswordFormValues {
  email: string;
}

export function ForgotPasswordPage(): JSX.Element {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const form = useForm<ForgotPasswordFormValues>({
    initialValues: {
      email: '',
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        if (!/^\S+@\S+$/.test(value)) return 'Invalid email address';
        return null;
      },
    },
  });

  const handleSubmit = useCallback(
    async (values: ForgotPasswordFormValues): Promise<void> => {
      setLoading(true);
      try {
        await medplum.post('auth/resetpassword', {
          email: values.email,
        });
        setEmailSent(true);
        showNotification({
          color: 'green',
          title: 'Email sent',
          message: 'Check your inbox for password reset instructions.',
        });
      } catch (err) {
        showNotification({
          color: 'red',
          title: 'Request failed',
          message: normalizeErrorString(err),
          autoClose: false,
        });
      } finally {
        setLoading(false);
      }
    },
    [medplum]
  );

  if (emailSent) {
    return (
      <AuthLayout title="Check Your Email" subtitle="We've sent password reset instructions to your email">
        <Stack gap="md">
          <Text ta="center" c="dimmed" size="sm">
            If you don&apos;t see the email in your inbox, please check your spam folder.
          </Text>

          <Button
            component={Link}
            to="/signin"
            fullWidth
            size="md"
            className={classes.submitButton}
          >
            Back to Sign In
          </Button>

          <Text ta="center" size="sm" c="dimmed">
            Didn&apos;t receive the email?{' '}
            <Anchor
              component="button"
              type="button"
              onClick={() => setEmailSent(false)}
              className={classes.resendLink}
            >
              Try again
            </Anchor>
          </Text>
        </Stack>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Forgot Password?"
      subtitle="Enter your email address and we'll send you instructions to reset your password"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Email Address"
            placeholder="Enter Email Address"
            leftSection={<IconMail size={16} />}
            size="md"
            {...form.getInputProps('email')}
          />

          <Button
            type="submit"
            fullWidth
            size="md"
            loading={loading}
            className={classes.submitButton}
          >
            Send Reset Link
          </Button>

          <Anchor
            component={Link}
            to="/signin"
            ta="center"
            size="sm"
            className={classes.backLink}
          >
            <IconArrowLeft size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
            Back to Sign In
          </Anchor>
        </Stack>
      </form>
    </AuthLayout>
  );
}
